import * as React from 'react';
import { IBac, IBacAction, BacActionStatus, BacActionType } from '../../models';
import { useData } from '../../core/context/ProvidersContext';
import styles from './BacActionHistoryModal.module.scss';

export interface IBacActionHistoryModalProps {
  bac: IBac;
  actions: IBacAction[];
  isAdmin: boolean;
  onClose: () => void;
  onDeleted: () => void;
}

const TYPE_META: Record<BacActionType, { label: string; icon: string; color: string }> = {
  relance_depot: { label: 'Relance dépôt',       icon: '📞', color: '#F9A825' },
  info_clients:  { label: 'Communication client', icon: '✉️', color: '#2B5EA7' },
};

const STATUS_META: Record<BacActionStatus, { label: string; cls: string }> = {
  confirme:    { label: 'Confirmé',          cls: 'statusConfirme' },
  en_attente:  { label: 'En attente retour', cls: 'statusEnAttente' },
  replanifie:  { label: 'Replanifié',        cls: 'statusReplanifie' },
  bloque:      { label: 'Bloqué',            cls: 'statusBloque' },
};

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatRelative(iso: string): string {
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (days === 0) return 'aujourd\'hui';
  if (days === 1) return 'hier';
  if (days < 7) return `il y a ${days} jours`;
  if (days < 30) return `il y a ${Math.floor(days / 7)} sem.`;
  if (days < 365) return `il y a ${Math.floor(days / 30)} mois`;
  return `il y a ${Math.floor(days / 365)} an(s)`;
}

function formatBytes(bytes: number | null): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

export const BacActionHistoryModal: React.FC<IBacActionHistoryModalProps> = ({
  bac, actions, isAdmin, onClose, onDeleted,
}) => {
  const data = useData();
  const [deletingId, setDeletingId] = React.useState<number | null>(null);

  const sortedActions = React.useMemo(
    () => [...actions].sort((a, b) => b.DoneAt.localeCompare(a.DoneAt)),
    [actions]
  );

  const handleDelete = async (action: IBacAction): Promise<void> => {
    if (!confirm('Supprimer cette entrée de l\'historique ? Cette action est irréversible.')) return;
    setDeletingId(action.Id);
    try {
      await data.deleteBacAction(action.Id);
      onDeleted();
    } catch (e) {
      alert('Erreur : ' + (e as Error).message);
    } finally {
      setDeletingId(null);
    }
  };

  const handleDownload = (action: IBacAction): void => {
    const url = data.getBacActionAttachmentUrl(action.Id);
    // Open in a new tab — Content-Disposition on the server triggers the download.
    window.open(url, '_blank');
  };

  const stats = React.useMemo(() => {
    const byType = sortedActions.reduce((acc, a) => {
      acc[a.Type] = (acc[a.Type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    const withAttachment = sortedActions.filter(a => a.HasAttachment).length;
    return { total: sortedActions.length, byType, withAttachment };
  }, [sortedActions]);

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <span className={styles.headerIcon}>📚</span>
            <div>
              <h3 className={styles.headerTitle}>Historique des actions</h3>
              <p className={styles.headerSubtitle}>
                {bac.DepotTitle} · Bac <strong>{bac.Title}</strong>
              </p>
            </div>
          </div>
          <button className={styles.close} onClick={onClose}>×</button>
        </div>

        <div className={styles.statsBar}>
          <div className={styles.stat}>
            <div className={styles.statValue}>{stats.total}</div>
            <div className={styles.statLabel}>Action{stats.total > 1 ? 's' : ''}</div>
          </div>
          <div className={styles.stat}>
            <div className={styles.statValue}>{stats.byType.relance_depot || 0}</div>
            <div className={styles.statLabel}>Relances dépôt</div>
          </div>
          <div className={styles.stat}>
            <div className={styles.statValue}>{stats.byType.info_clients || 0}</div>
            <div className={styles.statLabel}>Infos clients</div>
          </div>
          <div className={styles.stat}>
            <div className={styles.statValue}>{stats.withAttachment}</div>
            <div className={styles.statLabel}>Avec pièce jointe</div>
          </div>
        </div>

        <div className={styles.body}>
          {sortedActions.length === 0 ? (
            <div className={styles.empty}>
              <div className={styles.emptyIcon}>∅</div>
              <div className={styles.emptyTitle}>Aucune action enregistrée</div>
              <div className={styles.emptyDesc}>
                Les relances et communications effectuées sur ce bac apparaîtront ici.
              </div>
            </div>
          ) : (
            <div className={styles.timeline}>
              {sortedActions.map((action, idx) => {
                const typeMeta = TYPE_META[action.Type];
                const statusMeta = STATUS_META[action.Status];
                return (
                  <div
                    key={action.Id}
                    className={styles.entry}
                    style={{ animationDelay: `${idx * 0.04}s` }}
                  >
                    <div className={styles.rail}>
                      <div
                        className={styles.railDot}
                        style={{ background: typeMeta.color }}
                      >
                        <span>{typeMeta.icon}</span>
                      </div>
                      {idx < sortedActions.length - 1 && <div className={styles.railLine} />}
                    </div>

                    <div className={styles.card}>
                      <div className={styles.cardHeader}>
                        <div className={styles.cardHeaderLeft}>
                          <span
                            className={styles.typeTag}
                            style={{ background: typeMeta.color + '20', color: typeMeta.color }}
                          >
                            {typeMeta.label}
                          </span>
                          <span className={`${styles.statusTag} ${styles[statusMeta.cls]}`}>
                            {statusMeta.label}
                          </span>
                        </div>
                        {isAdmin && (
                          <button
                            className={styles.deleteBtn}
                            onClick={() => handleDelete(action)}
                            disabled={deletingId === action.Id}
                            title="Supprimer cette entrée"
                          >
                            {deletingId === action.Id ? '…' : '×'}
                          </button>
                        )}
                      </div>

                      <div className={styles.cardDateRow}>
                        <span className={styles.cardDate}>
                          <strong>{formatDate(action.DoneAt)}</strong>
                          <span className={styles.cardRelative}>({formatRelative(action.DoneAt)})</span>
                        </span>
                      </div>

                      {action.Comment && (
                        <p className={styles.cardComment}>{action.Comment}</p>
                      )}

                      {action.HasAttachment && (
                        <button
                          type="button"
                          className={styles.attachmentChip}
                          onClick={() => handleDownload(action)}
                        >
                          <span className={styles.attachmentIcon}>📎</span>
                          <span className={styles.attachmentInfo}>
                            <span className={styles.attachmentName}>
                              {action.AttachmentName || 'pièce jointe'}
                            </span>
                            {action.AttachmentSize && (
                              <span className={styles.attachmentMeta}>
                                {formatBytes(action.AttachmentSize)} · cliquer pour télécharger
                              </span>
                            )}
                          </span>
                          <span className={styles.attachmentDownload}>⤓</span>
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className={styles.footer}>
          <button className={styles.btnGhost} onClick={onClose}>Fermer</button>
        </div>
      </div>
    </div>
  );
};
